import { CovSensorStatus, FrostFreeStatus, PlanningType, SoundSensorStatus } from "@eisox/api/core-api";
import { z } from "@eisox/zod";

import { planningSchema } from "~/features/planning";
import type { Permission } from "~/types/auth.type";
import type { EnhancedRoom } from "~/types/room.type";

type RoomsFunctionSettingsSchemaType = z.infer<typeof roomsFunctionSettingsSchema>;
const roomsFunctionSettingsSchema = z.object({
  roomIds: z.array(z.string()).min(1),
  mode: z.enum(FrostFreeStatus).optional(),
  isSwitchEnabled: z.boolean().nullable(),
  isAutoPrecomf: z.boolean().nullable(),
  isPreComfRoom: z.boolean().nullable(),
  covSensorParam: z.object({
    status: z.enum(CovSensorStatus).nullable(),
    periods: z
      .array(planningSchema([PlanningType.ENABLED]))
      .min(0)
      .max(14),
  }),
  soundSensorParam: z.object({
    status: z.enum(SoundSensorStatus).nullable(),
  }),
});

const frostFreeMode = (rooms: EnhancedRoom[]): FrostFreeStatus.ENABLED | FrostFreeStatus.DISABLED | undefined => {
  if (rooms.length > 0) {
    // oxlint-disable-next-line typescript/no-unnecessary-condition -- runtime API guard despite OpenAPI typing
    if (rooms.every(r => r.frostFree?.status === FrostFreeStatus.ENABLED)) return FrostFreeStatus.ENABLED;
    // oxlint-disable-next-line typescript/no-unnecessary-condition -- runtime API guard despite OpenAPI typing
    if (rooms.every(r => r.frostFree?.status === FrostFreeStatus.DISABLED)) return FrostFreeStatus.DISABLED;
  }
  return undefined;
};

const unusualAbsenceDetectionOn = (rooms: EnhancedRoom[]): boolean | null => {
  const unsualAbsenceDetection = !!rooms[0]?.isAutoPrecomf;
  return rooms.length > 0 && rooms.every(r => r.isAutoPrecomf === unsualAbsenceDetection)
    ? unsualAbsenceDetection
    : null;
};

const preComfortTimeslotGenerationOn = (rooms: EnhancedRoom[]): boolean | null => {
  const preComfortTimeslotGeneration = !!rooms[0]?.isPreComfRoom;
  return rooms.length > 0 && rooms.every(r => r.isPreComfRoom === preComfortTimeslotGeneration)
    ? preComfortTimeslotGeneration
    : null;
};

const isSwitchEnabledOn = (rooms: EnhancedRoom[]): boolean | null => {
  const isSwitchEnabled = !!rooms[0]?.isSwitchEnabled;
  return rooms.length > 0 && rooms.every(r => r.isSwitchEnabled === isSwitchEnabled) ? isSwitchEnabled : null;
};

const covSensorParamStatusOn = (rooms: EnhancedRoom[]): CovSensorStatus | null => {
  // oxlint-disable-next-line typescript/no-unnecessary-condition -- runtime API guard despite OpenAPI typing
  const covSensorParamStatus = rooms[0]?.covSensorParam?.status;
  // oxlint-disable-next-line typescript/no-unnecessary-condition -- runtime API guard despite OpenAPI typing
  return rooms.length > 0 && rooms.every(r => r.covSensorParam?.status === covSensorParamStatus)
    ? (covSensorParamStatus ?? null)
    : null;
};

const soundSensorParamStatusOn = (rooms: EnhancedRoom[]): SoundSensorStatus | null => {
  // oxlint-disable-next-line typescript/no-unnecessary-condition -- runtime API guard despite OpenAPI typing
  const soundSensorParamStatus = rooms[0]?.soundSensorParam?.status;
  // oxlint-disable-next-line typescript/no-unnecessary-condition -- runtime API guard despite OpenAPI typing
  return rooms.length > 0 && rooms.every(r => r.soundSensorParam?.status === soundSensorParamStatus)
    ? (soundSensorParamStatus ?? null)
    : null;
};

const soundSensorParamToBoolean = (status: SoundSensorStatus | null): boolean | null =>
  status === null ? null : status === SoundSensorStatus.ENABLED;

const booleanToSoundSensorParam = (boolean: boolean | null): SoundSensorStatus | null =>
  boolean === null ? null : boolean ? SoundSensorStatus.ENABLED : SoundSensorStatus.DISABLED;

const canAccessRoomsFunctionSettings = (permissions: Permission): boolean =>
  !!permissions.room?.isSwitchEnabled?.update ||
  !!permissions.room?.isAutoPrecomf?.update ||
  !!permissions.room?.isPreComfRoom?.update;

export {
  booleanToSoundSensorParam,
  canAccessRoomsFunctionSettings,
  covSensorParamStatusOn,
  frostFreeMode,
  isSwitchEnabledOn,
  preComfortTimeslotGenerationOn,
  roomsFunctionSettingsSchema,
  soundSensorParamStatusOn,
  soundSensorParamToBoolean,
  unusualAbsenceDetectionOn,
};
export type { RoomsFunctionSettingsSchemaType };
